import { z } from "zod";

import { AddressObjectInputSchema, HotelInputSchema } from "@/lib/zod/__generated__/validation-schema";

import {
	geoLocationInputSchema,
	postalCodeZodSchema,
	s3ObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

export const hotelInfoInputZodSchema = HotelInputSchema.extend({
	name: z.string().min(1).max(50),
	catchCopy: z.string().min(1).max(1_000),

	logoImage: s3ObjectZodSchema,
	mainKeyVisual: s3ObjectZodSchema,
	keyVisuals: z.array(s3ObjectZodSchema),

	tel: z.string().min(1).max(15),
	email: z.string().email(),

	geoLocation: geoLocationInputSchema,

	address: AddressObjectInputSchema.extend({
		postalCode: postalCodeZodSchema.nullish(),
		pref: z
			.string()
			.min(1, { message: "都道府県を入力してください" })
			.max(4, { message: "都道府県は4文字以内で入力してください" }),
		city: z
			.string()
			.min(1, { message: "市区町村を入力してください" })
			.max(300, { message: "市区町村は300文字以内で入力してください" }),
		town: z
			.string()
			.min(1, { message: "市区町村以降を入力してください" })
			.max(300, { message: "市区町村以降は300文字以内で入力してください" }),
	}),

	/**
	 * GraphQL の @constraint(format: "uri") は空白文字を含む URL を拒否する。
	 * Zod の .url() は空白を %20 に正規化するため通ってしまうので、ここで弾く。
	 */
	twitterURL: z
		.string()
		.url()
		.regex(new RegExp(/(https:\/\/twitter.com\/.*)|(https:\/\/x.com\/.*)/), {
			message: "「https://twitter.com/」か「https://x.com/」から始まるURLを入力してください。",
		})
		.refine((value) => !/\s/.test(value), {
			message: "URLに空白文字を含めないでください。",
		})
		.or(z.literal(null).or(z.undefined()).or(z.literal(""))),

	facebookURL: z
		.string()
		.url()
		.regex(new RegExp(/(https:\/\/www.facebook.com\/.*)/), {
			message: "「https://www.facebook.com/」から始まるURLを入力してください。",
		})
		.refine((value) => !/\s/.test(value), {
			message: "URLに空白文字を含めないでください。",
		})
		.or(z.literal(null).or(z.undefined()).or(z.literal(""))),

	instagramURL: z
		.string()
		.url()
		.regex(new RegExp(/(https:\/\/www.instagram.com\/.*)/), {
			message: "「https://www.instagram.com/」から始まるURLを入力してください。",
		})
		.refine((value) => !/\s/.test(value), {
			message: "URLに空白文字を含めないでください。",
		})
		.or(z.literal(null).or(z.undefined()).or(z.literal(""))),

	threadsURL: z
		.string()
		.url()
		.regex(/^[\x20-\x7E]+$/, {
			message: "全角文字は使用できません。半角英数字で入力してください。",
		})
		.regex(new RegExp(/(https:\/\/www.threads.com\/.*)/), {
			message: "「https://www.threads.com/」から始まるURLを入力してください。",
		})
		.refine((value) => !/\s/.test(value), {
			message: "URLに空白文字を含めないでください。",
		})
		.or(z.literal(null).or(z.undefined()).or(z.literal(""))),

	lineURL: z
		.string()
		.url()
		.regex(new RegExp(/(https:\/\/line\.me\/.*)|(https:\/\/page\.line\.me\/.*)|(https:\/\/lin\.ee\/.*)/), {
			message: "LINE の URL（line.me / page.line.me / lin.ee）を入力してください。",
		})
		.refine((value) => !/\s/.test(value), {
			message: "URLに空白文字を含めないでください。",
		})
		.or(z.literal(null).or(z.undefined()).or(z.literal(""))),
});

export type HotelInfoZodSchemaType = z.infer<typeof hotelInfoInputZodSchema>;
